import React from "react";
import { GridCellParams, GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro";
import { Box, Grid } from "@material-ui/core";
import RankWeightToggle from "../../contestGenerator/RankWeightToggle";
import { ContestLineup, ContestPlayer } from "../../contestGenerator/store/types";
import { showStackContestGen } from "../../utils/stackConstants";
import { isTeamSport } from "../../utils/teamSportHelper";
import { SlateResponse } from "../store/types";
import { Badge, Divider, Stack } from "@mui/material";
import ConfirmedLineupIcons from "./ConfirmedLineupIcons";
import { CustomToolTip, toolTipText } from "../CustomToolTip";


const OnwershipBoostButtons = (props: { player: string, handleBoost: (playerAndId: string, boost: number) => void, boostedPlayers: { [key: string]: number } }) => {
  const { player, handleBoost, boostedPlayers } = props;
  const [increase, setIncrease] = React.useState(0);
  const [decrease, setDecrease] = React.useState(0);
  const handleIncrease = () => {
    if(boostedPlayers[player] !== 0){
      if (decrease < 0) {
        setDecrease(0);
        handleBoost(player, 0);
        setIncrease(0);
      } else {
      setDecrease(0);
      handleBoost(player, increase + 1);
      setIncrease(increase + 1);
      }
    }
  }
  const handleDecrease = () => {
    if(boostedPlayers[player] !== 0){   
      if (increase > 0) {
        setIncrease(0);
        handleBoost(player, 0);
        setDecrease(0);
      } else {
        setIncrease(0);
        handleBoost(player, decrease - 1);
        setDecrease(decrease - 1);
      }
    }
  }
  React.useEffect(() => {
    const boostedPlayer = boostedPlayers[player];
    if (boostedPlayer) {
      if (boostedPlayer >= 0) {
        setIncrease(boostedPlayer);
        setDecrease(0)
      } else if (boostedPlayer < 0) {
        setIncrease(0);
        setDecrease(boostedPlayer)
      }
    } else {
      setIncrease(0);
      setDecrease(0);
    }
  }, [boostedPlayers]);

  return (
    <Box display="flex" justifyContent="space-between" alignItems="center">
      {increase > 3 ? (
        <Badge badgeContent={increase} color="success" style={{ marginRight: 4 }}>
          <img
            src={`${process.env.PUBLIC_URL}/images/boost_positive_3.svg`}
            alt=""
            style={{ marginRight: 4, cursor: boostedPlayers[player] === 0 ? 'not-allowed' : 'pointer' }}
            onClick={handleIncrease}
          />
        </Badge>
      ) : (
        <img
          src={`${process.env.PUBLIC_URL}/images/boost_positive_${increase}.svg`}
          alt=""
          style={{ marginRight: 4, cursor: boostedPlayers[player] === 0 ? 'not-allowed' : 'pointer' }}
          onClick={handleIncrease}
        />
      )}
      {decrease < -3 ? (
        <Badge badgeContent={Math.abs(decrease)} color="error" sx={{ }}>
          <img
            src={`${process.env.PUBLIC_URL}/images/boost_negative_-3.svg`}
            alt=""
            style={{ cursor: boostedPlayers[player] === 0 ? 'not-allowed' : 'pointer' }}
            onClick={handleDecrease}
          />
          
        </Badge>
      ) : (
      <img
        src={`${process.env.PUBLIC_URL}/images/boost_negative_${decrease}.svg`}
        alt=""
        style={{ cursor: boostedPlayers[player] === 0 ? 'not-allowed' : 'pointer' }}
        onClick={handleDecrease}
      />
      )}
    </Box>
  )
}
const PlayerLockButtons = (props: { player: string, handleBoost: (playerAndId: string, boost: number, removePlayer) => void, boostedPlayers: { [key: string]: number } }) => {
  const { player, handleBoost, boostedPlayers } = props;
  const [locked, setLocked] = React.useState(false);

  const handleClick = () => {
    handleBoost(player, 0, !locked);
    setLocked(!locked);
  }

  React.useEffect(() => {
    const boostedPlayer = boostedPlayers[player];
    if (boostedPlayer === 0) {
      setLocked(true);
    } else {
      setLocked(false);
    }
  }, []);

  return (
    <Box display="flex" justifyContent="space-between" alignItems="center">
      <img
        src={`${process.env.PUBLIC_URL}/images/${locked ? 'add' : 'remove'}_player.svg`}
        alt=""
        style={{ marginRight: 4, cursor: 'pointer' }}
        onClick={handleClick}
      />
    </Box>
  )
}

export const contestGeneratorPlayerHeaderConfig = (handleBoost: (playerAndId: string, boost: number, removePlayer?: boolean) => void, boostedPlayers: { [key: string]: number }, slate: SlateResponse, liveContest: boolean): GridColDef[] => {
  const sport = slate?.sport;
  let headers: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: .25,
      headerAlign: 'left',
      cellClassName: (params: GridCellParams<any, number>) => {
        const nameAndId = params.row.nameAndId + '|' + params.row.position

        if (boostedPlayers[nameAndId] === 0) {
          return 'removed';
        }
        return '';
      },
      renderCell(params: GridRenderCellParams<ContestPlayer>) {
        const { name, confirmedLineup, lineupPosition } = params.row;
        return (
          <Box display="flex" justifyContent="center" alignItems="center">
            {!liveContest && (
              <Box><PlayerLockButtons player={params.row.nameAndId + '|' + params.row.position} handleBoost={handleBoost} boostedPlayers={boostedPlayers} /></Box>
            )}
            <Box fontSize='12px' fontWeight={700} style={{ textTransform: 'uppercase'}}>
              <span style={{ color: 'rgba(255, 255, 255, 0.70)' }}>{name?.split(' ')[0]}</span> <span style={{ fontSize: '14px', }}>{name?.split(' ')?.splice(1).join(' ')}</span>
            </Box>
            {
              slate?.sport?.toLowerCase() === 'mlb' && (
                <Box paddingLeft='4px'>
                  <ConfirmedLineupIcons confirmed={confirmedLineup} battingOrder={lineupPosition}/>
                </Box>
              )
            }
          </Box>
        )
      },
    },
    {
      field: 'position',
      headerName: 'Pos.',
      headerAlign: 'left',
      cellClassName: (params: GridCellParams<any, number>) => {
        const nameAndId = params.row.nameAndId + '|' + params.row.position
        if (boostedPlayers[nameAndId] === 0) {
          return 'removed';
        }
        return '';
      },
    },
    {
      field: 'team',
      headerName: 'Team',
      headerAlign: 'left',
      cellClassName: (params: GridCellParams<any, number>) => {
        const nameAndId = params.row.nameAndId + '|' + params.row.position
        if (boostedPlayers[nameAndId] === 0) {
          return 'removed';
        }
        return '';
      },
      renderCell(params) {
        const { team, teamLogo } = params.row;
        return (
          <Box display="flex" justifyContent="center" alignItems="center">
            { teamLogo && (
              <Box height="25px" width="25px" marginRight="4px" style={{ backgroundColor: ['det', 'sd', 'col'].indexOf(team?.toLowerCase()) > -1 ? 'white' : '', borderRadius: ['det', 'sd', 'col'].indexOf(team?.toLowerCase()) > -1 ? '50%' : '', padding: ['det', 'sd', 'col'].indexOf(team?.toLowerCase()) > -1 ? '1px' : '', }}>
                <img src={teamLogo} height="100%" width="100%" style={{ objectFit: 'contain' }} alt="team" />
              </Box>
            )}
            <div>{team}</div>
          </Box>
        )
      },
    },
    {
      field: 'opponent',
      headerName: 'Opp',
      headerAlign: 'left',
      cellClassName: (params: GridCellParams<any, number>) => {
        const nameAndId = params.row.nameAndId + '|' + params.row.position
        if (boostedPlayers[nameAndId] === 0) {
          return 'removed';
        }
        return '';
      },
      renderCell(params) {
        const { opponent, opponentLogo } = params.row;
        return (
          <Box display="flex" justifyContent="center" alignItems="center">
            {opponentLogo && (
              <Box height="25px" width="25px" marginRight="4px" style={{ backgroundColor: ['det', 'sd', 'col'].indexOf(opponent?.toLowerCase()) > -1 ? 'white' : '', borderRadius: ['det', 'sd', 'col'].indexOf(opponent?.toLowerCase()) > -1 ? '50%' : '', padding: ['det', 'sd', 'col'].indexOf(opponent?.toLowerCase()) > -1 ? '1px' : '', }}>
                <img src={opponentLogo} height="100%" width="100%" style={{ objectFit: 'contain' }} alt={opponent} />
              </Box>
            )}
            <div>{opponent}</div>
          </Box>
        )
      },
    },
    {
      field: 'ownership',
      headerName: 'Ownership',
      flex: .25,
      headerAlign: 'center',
      type: 'number',
      align: 'center',
      cellClassName: (params: GridCellParams<any, number>) => {
        const nameAndId = params.row.nameAndId + '|' + params.row.position
        if (boostedPlayers[nameAndId] === 0) {
          return 'removed';
        }
        return '';
      },
      valueParser(value, params) {
        return value / 100;
      },
      valueFormatter(params) {
        if (isNaN(params.value)) {
          return '-'
        }
        const percentage = (params.value * 100)?.toFixed(1);
        return percentage + '%';
      },
      renderCell(params) {
        return (
          <Stack fontWeight={800}>
            {params.formattedValue && !isNaN(params.value) ? params.formattedValue : '-'}
          </Stack>
        )
      },
      renderHeader(params) {
        return (
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <CustomToolTip title={toolTipText.ownership}>
              <Box>Ownership</Box>
            </CustomToolTip>
          </Box>
        )
      },
    },
    {
      field: 'exposure',
      headerName: 'Pool Exposure',
      flex: .25,
      headerAlign: 'center',
      type: 'number',
      align: 'center',
      cellClassName: (params: GridCellParams<any, number>) => {
        const nameAndId = params.row.nameAndId + '|' + params.row.position
        if (boostedPlayers[nameAndId] === 0) {
          return 'removed';
        }
        return '';
      },
      valueParser(value, params) {
        return value / 100;
      },
      valueFormatter(params) {
        if (isNaN(params.value)) {
          return null
        }
        const percentage = (params.value * 100)?.toFixed(1);
        return percentage + '%';
      },
      renderCell(params) {
        const percentage = (params.value * 100)?.toFixed(1) ;

        return (
          <Stack fontWeight={800}>
            {percentage ? percentage + '%' : '-'}
          </Stack>
        )
      },
      renderHeader(params) {
        return (
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <CustomToolTip title={toolTipText.poolExposure}>
              <Box>Pool Exposure</Box>
            </CustomToolTip>
          </Box>
        )
      },
    },
    {
      field: 'difference',
      headerName: 'Difference',
      flex: .25,
      headerAlign: 'center',
      type: 'number',
      align: 'center',
      cellClassName: (params: GridCellParams<any, number>) => {
        const nameAndId = params.row.nameAndId + '|' + params.row.position
        if (boostedPlayers[nameAndId] === 0) {
          return 'removed';
        }
        return '';
      },
      valueGetter(params) {
        const { exposure, ownership } = params.row;
        return exposure - ownership;
      },
      valueParser(value, params) {
        return value / 100;
      },
      valueFormatter(params) {
        if (isNaN(params.value)) {
          return '-'
        }
        const percentage = (params.value * 100)?.toFixed(1);
        return percentage + '%';
      },
      renderCell(params) {
        if (isNaN(params.value)) {
          return '-'
        }
        const percentage = (params.value * 100)?.toFixed(1) ;

        return (
          <Stack fontWeight={800}>
            {percentage ? percentage + '%' : '-'}
          </Stack>
        )
      },
      renderHeader(params) {
        return (
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <CustomToolTip title={toolTipText.difference}>
              <Box>Difference</Box>
            </CustomToolTip>
          </Box>
        )
      },
    },
    {
      field: 'boost',
      headerName: 'Boost Ownership',
      headerAlign: 'center',
      align: 'center',
      flex: .1,
      cellClassName: (params: GridCellParams<any, number>) => {
        const nameAndId = params.row.nameAndId + '|' + params.row.position
        if (boostedPlayers[nameAndId] === 0) {
          return 'removed';
        }
        return '';
      },
      renderCell: (params: GridRenderCellParams<ContestPlayer>) => <OnwershipBoostButtons player={`${params.row.nameAndId}|${params.row.position}`} handleBoost={handleBoost} boostedPlayers={boostedPlayers} />,
      renderHeader(params) {
        return (
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <CustomToolTip title={toolTipText.boostOwnership}>
              <Box>Boost Ownership</Box>
            </CustomToolTip>
          </Box>
        )
      },
    }
  ];
  if (!isTeamSport(sport)) {
    if (slate?.type?.toUpperCase() !== "SHOWDOWN") {
      headers.splice(1, 3);
    }
    else {
      headers.splice(2, 2);
    }
  }
  if (liveContest) {
    headers = headers.filter(x => ['ownership', 'difference', 'boost'].indexOf(x.field) === -1);
  }
  return headers;
};

export const contestGeneratorLineupHeaderConfig = (projectionMultiplier: number, ownershipMultiplier: number, updateProjMultiplier: (updatedProj: number) => void, updateOwnMultiplier: (updatedOwn: number) => void, slate: SlateResponse | undefined, data: string): GridColDef[] => {
  const headers: GridColDef[] = [
    {
      field: 'projectionRank',
      headerName: 'Proj. Rank',
      type: 'number',
      headerAlign: 'left',
      align: 'left',
      width: 240,
      renderHeader(params) {
        return (
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <>
              Proj. Rank
            </>
            <RankWeightToggle onChange={(newPct: number) => updateProjMultiplier(newPct / 100)} previousPercent={projectionMultiplier} />
          </Box>
        )
      },
    },
    {
      field: 'ownershipRank',
      headerName: 'Own. Rank',
      type: 'number',
      headerAlign: 'left',
      align: 'left',
      width: 240,
      renderHeader(params) {
        return (
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <>
              Own Rank
            </>
            <RankWeightToggle onChange={(newPct: number) => updateOwnMultiplier(newPct / 100)} previousPercent={ownershipMultiplier} />
          </Box>
        )
      },
    },
    {
      field: 'overallRank',
      headerName: 'Overall Rank',
      type: 'number',
      headerAlign: 'left',
      align: 'left',
    },
    {
      field: 'totalProjection',
      headerName: 'Lineup Proj',
      type: 'number',
      headerAlign: 'left',
      align: 'left',
    },
    {
      field: 'totalOwnership',
      headerName: 'Total Ownership',
      headerAlign: 'left',
      type: 'number',
      align: 'left',
      valueParser(value, params) {
        return value / 100;
      },
      valueFormatter(params) {
        if (isNaN(params.value)) {
          return null
        }
        const percentage = (params.value * 100)?.toFixed(1);
        return percentage + '%';
      },
    },
    {
      field: 'salary',
      headerName: 'Salary',
      type: 'number',
      headerAlign: 'center',
      align:'center',
    },
    {
      field: 'stack',
      headerName: 'Stack Type',
      headerAlign: 'left',
      flex: .1,
      valueGetter(params) {
        const { stack } = params.row;
        return stack.type + stack.teams.map;
      },
      renderCell(params) {
        const { stack } = params.row;
        let newStackType = (stack.type as string).split('/');
        let teams = stack.teams;
        // Remove the last element if it the stack type ends in - 1
        if(teams && stack.type.endsWith('-1')){
          teams = teams?.slice(0, -1);
        }
        if (slate?.type?.toUpperCase() === "SHOWDOWN") {
          newStackType = (stack.type as string).split('-')
          return (
            <Box display="flex" justifyContent="center" alignItems="center" width="100%">
              {
              teams.map((x, i) => (
                  <>
                    {x.logo && (
                      <Box height="24px" width="24px" marginRight="4px" style={{ backgroundColor: ['det', 'sd', 'col'].indexOf(x.name?.toLowerCase()) > -1 ? 'white' : '', borderRadius: ['det', 'sd', 'col'].indexOf(x.name?.toLowerCase()) > -1 ? '50%' : '', padding: ['det', 'sd', 'col'].indexOf(x.name?.toLowerCase()) > -1 ? '1px' : '', }}>
                        <img src={x.logo} height="100%" width="100%" style={{ objectFit: 'contain', marginRight: '2px', }} alt="team" />
                      </Box>
                    )}
                    {(newStackType.length > i) && <div style={{overflow:'hidden', textOverflow: 'ellipsis'}}>{newStackType[i]}</div>}
                  </>
                ))
              }
            </Box>
          )
        } else {
          return (
            <Stack spacing={1} direction={'row'} display="flex" justifyContent="center" alignItems="center" width="100%">
              <div style={{overflow:'hidden', textOverflow: 'ellipsis'}}>{newStackType.join('-')}</div>
              {
                teams?.map((x, i) => (
                  <>
                    {x.logo && (
                      <Box height="24px" width="24px" marginRight="4px" style={{ backgroundColor: ['det', 'sd', 'col'].indexOf(x.name?.toLowerCase()) > -1 ? 'white' : '', borderRadius: ['det', 'sd', 'col'].indexOf(x.name?.toLowerCase()) > -1 ? '50%' : '', padding: ['det', 'sd', 'col'].indexOf(x.name?.toLowerCase()) > -1 ? '1px' : '', }}>
                        <img src={x.logo} height="100%" width="100%" style={{ objectFit: 'contain', marginRight: '2px', }} alt="team" />
                      </Box>
                    )}
                    
                  </>
                ))
              }
            </Stack>
          )
        }
      },
    },
    // {
    //   field: 'teams',
    //   headerName: 'Teams',
    //   headerAlign: 'left',
    //   flex: .08,
    //   valueGetter(params) {
    //     const { stack } = params.row;
    //     return stack;
    //   },
    //   renderCell(params) {
    //     const { stack } = params.row;
    //     return (
    //       <Box display="flex" justifyContent="center" alignItems="center">
    //         {
    //           stack?.teams?.map((x, i) => (
    //             <>
    //         <Box height="30px" width="30px" marginRight="4px" style={{ backgroundColor: ['det', 'sd', 'col'].indexOf(x.name?.toLowerCase()) > -1 ? 'white': '', borderRadius: ['det', 'sd', 'col'].indexOf(x.name?.toLowerCase()) > -1 ? '50%': '', padding:  ['det', 'sd', 'col'].indexOf(x.name?.toLowerCase()) > -1 ? '1px': '', }}>
    //                 <img src={x.logo} height="100%" width="100%" style={{ objectFit: 'contain' }} alt="team" />
    //               </Box>
    //               <div>{`${x.name} ${i !== stack.teams.length -1 ? '-' : ''}`}</div>
    //             </>
    //           ))
    //         }
    //       </Box>
    //     )
    //   },
    // },
    {
      field: 'dupes',
      headerName: 'Dupes',
      type: 'number',
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'lineups',
      headerName: 'Lineup',
      headerAlign: 'left',
      align: 'left',
      flex: .45,
      valueGetter: (params: GridRenderCellParams<ContestLineup>) => {
        return params.row.lineup.map((x) => `${x.name} (${x.position})`).join(' ')
      },
      renderCell: (params: GridRenderCellParams<ContestLineup>) => {
        const players = params.row.lineup.map((x, index) => (
          <Box display="flex" justifyContent="center" alignItems="center" paddingRight={isTeamSport(slate?.sport) ? 0 : 1}>
            {x.teamLogo && (
              <Box display="flex" height="14px" width="14px" marginRight="4px" marginLeft="4px">
                <img src={x.teamLogo} height="100%" width="100%" style={{ objectFit: 'contain' }} alt="team" />
              </Box>
            )}
            <span style={{ color: '#43BBFF', fontWeight: 900, fontSize: '10px', marginRight: '2px' }}>{isTeamSport(slate?.sport) ? ` ${x.position}` : ''}</span> {`${x.name}`}
            {
              index !== params.row.lineup.length - 1 && (
                <Divider orientation="vertical" flexItem variant='middle' sx={{ borderColor: 'rgba(255, 255, 255, 0.60)', mx: '4px !important', height: '8px', borderRadius: '10px' }}/>
              )
            }
          </Box>
        ));
        return (
          <Grid style={{ padding: 2, fontSize: '16px', width: '100%', }}>
            <Grid item xs={12} style={{ fontSize: '11px', display: 'flex', flexWrap: 'wrap' }}>
              {players}
            </Grid>
          </Grid>
        )
      }
    },
  ];
  let finalColumns = headers;
  if (data.includes('\n')) {
    const headerData = data.split('\n')[0].split(',');
    const addlColumns = headerData.map((x, i) => {
      return {
        field: `HIDE${i}`,
        headerName: x,
        valueGetter(params) {
          const { lineup } = params.row;
          return lineup[i].nameAndId;
        },
      } as GridColDef;
    });
    finalColumns = headers.concat(addlColumns);
  }
  if (!showStackContestGen(slate!)) {
    finalColumns.splice(6, 1)
  }
  return finalColumns;
}

export const contestGeneratorStackHeaderConfig: GridColDef[] = [
  {
    field: 'stackType',
    headerName: 'Stack Types',
    headerAlign: 'left',
    flex: .25,
    align: 'left',
  },
  {
    field: 'exposurePct',
    headerName: 'Pool Exposure',
    headerAlign: 'left',
    flex: .25,
    align: 'left',
  },
  {
    field: 'desiredExposure',
    headerName: 'Desired Exposure',
    headerAlign: 'left',
    flex: .25,
    align: 'left',
  },
  {
    field: 'difference',
    headerName: 'Difference',
    headerAlign: 'left',
    flex: .25,
    align: 'left',
  },
];